import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typography, Checkbox, Button } from '@percent/lemonade'
import { SmartApplyPartner } from '@percent/cause-dashboard/constants/smartApplyPartners'
import { useSmartApplyTracking } from '@percent/cause-dashboard/common/hooks/useSmartApplyTracking'
import { useMetaOfferAllowedInCountry } from '@percent/cause-dashboard/common/hooks/useMetaOfferAllowedInCountry/useMetaOfferAllowedInCountry'
import { POSTHOG_FEATURE_FLAGS, usePostHogFeatureFlag } from '@percent/cause-dashboard/common/hooks'
import { SMART_APPLY_PARTNER_LOGOS } from '../../explicitSmartApply/smartApplyPartnerLogos/SmartApplyPartnerLogos'
import styles from '../../explicitSmartApply/smartApplyShared.module.scss'

// Convert camelCase to kebab-case for test IDs
const toKebabCase = (str: string) => str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase()

type SecondSmartApplyPageProps = {
  partners: SmartApplyPartner[]
  setSelectedPartner: (partner: SmartApplyPartner) => void
  appliedPartners: Set<SmartApplyPartner>
  onApply: (action: 'applied') => void
  onDismiss: (action: 'dismissed') => void
  registerDismissCallback?: (callback: (() => void) | null) => void
}

export function SecondSmartApplyPage({
  partners,
  setSelectedPartner,
  appliedPartners,
  onApply,
  onDismiss,
  registerDismissCallback
}: Readonly<SecondSmartApplyPageProps>) {
  const { t } = useTranslation()
  const { trackProductApplied, trackDismissed } = useSmartApplyTracking({ variant: 'second_smart_apply' })
  const isMetaSmartApplyEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_META_SMART_APPLY)
  const isMetaAllowedInCountry = useMetaOfferAllowedInCountry(isMetaSmartApplyEnabled)
  const shouldShowMeta = isMetaSmartApplyEnabled && isMetaAllowedInCountry
  const isAdobeExpressEnabled = usePostHogFeatureFlag(POSTHOG_FEATURE_FLAGS.CP_ADOBE_EXPRESS_SECOND_SMART_APPLY)

  const partnerSet = useMemo(() => new Set(partners), [partners])

  const products = useMemo(() => {
    const allProducts: { name: SmartApplyPartner; displayName: string; description: string; logo: string }[] = [
      {
        name: 'claude',
        displayName: t('typography.smartApply.claude.title'),
        description: t('typography.smartApply.claude.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.claude
      },
      {
        name: 'adobe',
        displayName: t('typography.smartApply.adobe.title'),
        description: t('typography.smartApply.adobe.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.adobe
      },
      {
        name: 'openai',
        displayName: t('typography.smartApply.openai.title'),
        description: t('typography.smartApply.openai.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.openai
      },
      {
        name: 'zoom',
        displayName: t('typography.smartApply.zoom.title'),
        description: t('typography.smartApply.zoom.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.zoom
      },
      {
        name: 'meta',
        displayName: t('typography.smartApply.meta.title'),
        description: t('typography.smartApply.meta.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.meta
      },
      {
        name: 'lovable',
        displayName: t('typography.smartApply.lovable.title'),
        description: t('typography.smartApply.lovable.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.lovable
      },
      {
        name: 'hootsuite',
        displayName: t('typography.smartApply.hootsuite.title'),
        description: t('typography.smartApply.hootsuite.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.hootsuite
      },
      {
        name: 'adobeExpress',
        displayName: t('typography.smartApply.adobeExpress.title'),
        description: t('typography.smartApply.adobeExpress.description'),
        logo: SMART_APPLY_PARTNER_LOGOS.adobeExpress
      }
    ]

    return allProducts.filter(p => {
      if (!partnerSet.has(p.name)) return false
      if (p.name === 'meta' && !shouldShowMeta) return false
      if (p.name === 'adobeExpress' && !isAdobeExpressEnabled) return false
      return true
    })
  }, [t, partnerSet, shouldShowMeta, isAdobeExpressEnabled])

  const [applied, setApplied] = useState<Set<SmartApplyPartner>>(new Set(appliedPartners))

  useEffect(() => {
    setApplied(new Set(appliedPartners))
  }, [appliedPartners])

  useEffect(() => {
    registerDismissCallback?.(() => {
      trackDismissed({ appliedProductsCount: appliedPartners.size })
    })
    return () => registerDismissCallback?.(null)
  }, [registerDismissCallback, trackDismissed, appliedPartners.size])

  const handleProductToggle = (partnerName: SmartApplyPartner) => {
    setApplied(prev => new Set(prev).add(partnerName))
    setSelectedPartner(partnerName)
    trackProductApplied({ productName: partnerName })
    onApply('applied')
  }

  const handleDismiss = () => {
    trackDismissed({ appliedProductsCount: applied.size })
    onDismiss('dismissed')
  }

  const getButtonText = () => {
    if (applied.size === 0) return t('typography.smartApply.notInterested')
    return t('button.close')
  }

  return (
    <div className={styles.smartApplyContainer}>
      <div className={styles.contentWrapper}>
        <Typography className={styles.title} variant="h2">
          <span role="img" aria-label="sparkles">
            ✨
          </span>{' '}
          {t('typography.smartApply.title')}
        </Typography>

        <div className={styles.subtitles}>
          <Typography variant="large">
            <div
              dangerouslySetInnerHTML={{
                __html: t('typography.smartApply.subtitle1')
              }}
            />
          </Typography>
        </div>

        <div className={styles.checkboxesContainer}>
          {products.map(product => {
            const isApplied = applied.has(product.name)

            return (
              <button
                type="button"
                key={product.name}
                className={`${styles.productCard} ${isApplied ? styles.appliedCard : ''}`}
                disabled={isApplied}
                onClick={() => {
                  if (!isApplied) handleProductToggle(product.name)
                }}
                onKeyDown={e => {
                  if (!isApplied && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault()
                    handleProductToggle(product.name)
                  }
                }}
                data-testid={`${toKebabCase(product.name)}-product-card`}
              >
                <img src={product.logo} alt={product.displayName} className={styles.productLogo} />
                <div className={styles.productInfo}>
                  <Typography variant="default-bold" className={styles.productTitle}>
                    {product.displayName}
                  </Typography>
                  <Typography $color="gray300" variant="default" className={styles.productDescription}>
                    {product.description}
                  </Typography>
                </div>
                {isApplied ? (
                  <Checkbox
                    name={product.name}
                    variant="default"
                    label=""
                    onChange={() => handleProductToggle(product.name)}
                    active
                    dataTestId={`${toKebabCase(product.name)}-checkbox`}
                    className={styles.productCheckbox}
                  />
                ) : (
                  <Typography variant="default" className={styles.clickToApply}>
                    {t('button.apply')}
                  </Typography>
                )}
              </button>
            )
          })}
        </div>
      </div>

      <div className={`${styles.buttonWrapper} ${styles.applyButtonWrapper}`}>
        <Button variant="tertiary" stretch onClick={handleDismiss} data-testid="second-smart-apply-dismiss-button">
          {getButtonText()}
        </Button>
      </div>
    </div>
  )
}
